<section class="translations-page">
    <div class="translations-header">
        <div class="section-header">
            <h1 class="page-title">Shine translations</h1>
      <p class="translations-muted">
          Translate HubSpot CMS content and keep language variants in sync.
      </p>
    </div>
  </div>

  <mat-tab-group [selectedIndex]="selectedTabIndex">
    <mat-tab label="Pages">
      <div class="tab-section">
          <div class="setup-grid">
              <mat-card class="translations-card">
                  <mat-card-header>
                      <div class="card-title-row">
                          <p class="translations-section-title">Job setup</p>
                          <span class="translations-muted">Choose a connection and language before translating.</span>
                      </div>
                  </mat-card-header>
                  <mat-card-content>
                      <div class="form-grid">
                          <mat-form-field appearance="outline">
                              <mat-label>HubSpot connection</mat-label>
                              <mat-select [(ngModel)]="selectedConnectionId">
                                  @for (connection of connections; track connection.id) {
                                      <mat-option [value]="connection.id">
                                          {{ connection.name || connection.id }}
                                      </mat-option>
                                  }
                              </mat-select>
                              <mat-hint>Uses Shine Auth connections</mat-hint>
                          </mat-form-field>

                          <mat-form-field appearance="outline">
                              <mat-label>Content type</mat-label>
                              <mat-select [(ngModel)]="contentType">
                                  <mat-option value="SitePage">Site pages</mat-option>
                                  <mat-option value="LandingPage">Landing pages</mat-option>
                                  <mat-option value="BlogPost">Blog posts</mat-option>
                              </mat-select>
                          </mat-form-field>

                          <mat-form-field appearance="outline">
                              <mat-label>Target language</mat-label>
                              <input
                                      [(ngModel)]="targetLanguage"
                                      [matAutocomplete]="languagePicker"
                                      matInput
                                      placeholder="Search language"
                              />
                              <mat-autocomplete #languagePicker="matAutocomplete" autoActiveFirstOption>
                                  @for (option of filteredLanguages(); track option.code) {
                                      <mat-option [value]="option.code">{{ displayLanguage(option) }}</mat-option>
                                  }
                              </mat-autocomplete>
                          </mat-form-field>
                      </div>

                      <mat-form-field appearance="outline" class="full-width">
                          <mat-label>Job instructions (optional)</mat-label>
                          <textarea [(ngModel)]="jobInstructions" matInput rows="4"></textarea>
                          <mat-hint>Client defaults will be applied automatically.</mat-hint>
                      </mat-form-field>

                      <div class="action-row">
                          <button (click)="loadPages(true)" [disabled]="pagesLoading" color="primary" mat-flat-button>
                              Load pages
                          </button>
                          <button (click)="refreshConnections()" mat-stroked-button>Refresh connections</button>
                      </div>
                  </mat-card-content>
              </mat-card>

              <mat-card class="translations-card">
                  <mat-card-header>
                      <div class="card-title-row">
                          <p class="translations-section-title">Auto-sync</p>
                          <span class="translations-muted">Keep Shine language variants in sync when HubSpot pages change.</span>
                      </div>
                  </mat-card-header>
                  <mat-card-content>
                      <div class="form-grid">
                          <mat-form-field appearance="outline">
                              <mat-label>Sync connection</mat-label>
                              <mat-select (selectionChange)="saveSettings()" [(ngModel)]="settings.syncConnectionId">
                                  @for (connection of connections; track connection.id) {
                                      <mat-option [value]="connection.id">
                                          {{ connection.name || connection.id }}
                                      </mat-option>
                                  }
                              </mat-select>
                              <mat-hint>Uses the selected HubSpot portal for polling</mat-hint>
                          </mat-form-field>
                      </div>

                      <div class="sync-mode">
                          <p class="sync-label">Sync mode</p>
                          <mat-button-toggle-group
                                  (change)="setSyncMode($event.value)"
                                  [value]="getSyncConfig(contentType).mode"
                                  aria-label="Sync mode"
                                  class="sync-mode-toggle"
                          >
                              @for (option of syncModeOptions; track option.value) {
                                  <mat-button-toggle [value]="option.value">{{ option.label }}</mat-button-toggle>
                              }
                          </mat-button-toggle-group>
                          <p class="translations-muted sync-summary">
                              Included: {{ getSyncConfig(contentType).includeIds.length }} · Excluded:
                              {{ getSyncConfig(contentType).excludeIds.length }}. Manage lists from the page grid below.
                          </p>
                      </div>

                      <mat-form-field appearance="outline" class="full-width auto-language-field">
                          <mat-label>Auto-translate languages</mat-label>
                          <mat-chip-grid #autoLanguageGrid aria-label="Auto-translate languages">
                              @for (lang of settings.autoTranslateLanguages; track lang) {
                                  <mat-chip-row (removed)="removeAutoLanguage(lang)">
                                      {{ formatAutoLanguage(lang) }}
                                      <button aria-label="Remove language" matChipRemove type="button">
                                          <mat-icon>cancel</mat-icon>
                                      </button>
                                  </mat-chip-row>
                              }
                              <input
                                      (matChipInputTokenEnd)="addAutoLanguageFromInput($event)"
                                      [(ngModel)]="autoLanguageInput"
                                      [matAutocomplete]="autoLanguagePicker"
                                      [matChipInputAddOnBlur]="false"
                                      [matChipInputFor]="autoLanguageGrid"
                                      [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                                      placeholder="Add language"
                              />
                          </mat-chip-grid>
                          <mat-autocomplete #autoLanguagePicker="matAutocomplete"
                                            (optionSelected)="selectAutoLanguage($event.option.value)">
                              @for (option of filteredAutoLanguages(); track option.code) {
                                  <mat-option [value]="option.code">{{ displayLanguage(option) }}</mat-option>
                              }
                          </mat-autocomplete>
                          <mat-hint>These languages will be created and kept in sync for eligible pages.</mat-hint>
                      </mat-form-field>

                      <div class="action-row">
                          <button (click)="runSyncNow()" [disabled]="!isSyncEnabled()" color="primary"
                                  mat-stroked-button>
                              Run sync now
                          </button>
                      </div>
                  </mat-card-content>
              </mat-card>
          </div>

          <mat-card class="translations-card">
          <mat-card-header>
            <div class="card-title-row">
              <p class="translations-section-title">Available pages</p>
              <span class="translations-muted">Drafts and published pages can be translated into new variants.</span>
            </div>
          </mat-card-header>
          <mat-card-content>
            @if (pagesLoading) {
              <div class="loading-row">
                <mat-progress-spinner diameter="28" mode="indeterminate"></mat-progress-spinner>
                <span class="translations-muted">Loading pages...</span>
              </div>
            } @else {
              @if (pagesLoadError) {
                <div class="translations-error" role="alert">
                  <p class="translations-muted">{{ pagesLoadError }}</p>
                  <button type="button" (click)="loadPages(true)" [disabled]="pagesLoading" mat-stroked-button>
                    Retry
                  </button>
                </div>
              } @else if (pages.length === 0) {
                <p class="translations-muted">No pages loaded yet. Choose a connection and click “Load pages”.</p>
              } @else {
                <div class="page-list">
                  @for (page of pages; track page.id) {
                    <div class="page-row">
                        <div class="page-main">
                            <div class="page-title-row">
                                <p class="page-title">{{ page.name }}</p>
                                @if (syncStatusLabel(page)) {
                                    <span class="sync-pill">{{ syncStatusLabel(page) }}</span>
                                }
                            </div>
                        <p class="translations-muted">
                          {{ formatLanguage(page.language) }} · {{ page.state || 'draft' }}
                          · {{ page.updatedAt ? (page.updatedAt | date: 'medium') : '—' }}
                        </p>
                            <div class="page-meta">
                          <span class="meta-pill">
                            Shine variants: {{ page.shineVariations?.length || 0 }}
                          </span>
                                @if (page.outOfSyncVariations?.length) {
                                    <span class="meta-pill meta-pill--warn">
                              External edits: {{ page.outOfSyncVariations?.length }}
                            </span>
                                }
                            </div>
                        @if (page.url) {
                          <a class="page-link" [href]="page.url" target="_blank" rel="noreferrer">Open in HubSpot</a>
                        }
                        @if (hasVariations(page)) {
                          <div class="page-variations">
                            <div class="variation-group">
                              <p class="variation-title">Shine variations</p>
                              @if (page.shineVariations?.length) {
                                <div class="variation-list">
                                  @for (variation of page.shineVariations; track variation.id) {
                                    <div class="variation-pill variation-pill--shine">
                                      <span>{{ variationLabel(variation) }}</span>
                                        @if (isExternallyEdited(page, variation)) {
                                            <span class="variation-flag">Edited outside Shine</span>
                                        }
                                      @if (variation.updatedAt) {
                                        <span class="variation-date">{{ variation.updatedAt | date: 'short' }}</span>
                                      }
                                      @if (variation.url) {
                                        <a class="variation-link" [href]="variation.url" target="_blank" rel="noreferrer">
                                          Open
                                        </a>
                                      }
                                      <button
                                        mat-button
                                        class="variation-action"
                                        type="button"
                                        (click)="retranslateVariation(page, variation)"
                                      >
                                        Retranslate
                                      </button>
                                    </div>
                                  }
                                </div>
                              } @else {
                                <span class="translations-muted">No Shine variants.</span>
                              }
                            </div>
                          </div>
                        }
                        </div>
                        <div class="page-actions">
                            <button
                                    (click)="translatePage(page)"
                                    [disabled]="!canTranslate()"
                                    color="primary"
                                    mat-flat-button
                            >
                                Translate
                            </button>
                            <button [matMenuTriggerFor]="pageMenu" mat-stroked-button type="button">
                                Actions
                            </button>
                            <mat-menu #pageMenu="matMenu">
                                <button (click)="runSyncForPage(page)" [disabled]="!isSyncEnabled()" mat-menu-item>
                                    Sync now
                                </button>
                                <button (click)="toggleSync(page)" [disabled]="!isSyncEnabled()" mat-menu-item>
                                    {{ syncActionLabel(page) }}
                                </button>
                            </mat-menu>
                        </div>
                    </div>
                  }
                </div>
                @if (pagesNext) {
                  <div class="action-row">
                    <button mat-stroked-button (click)="loadPages(false)" [disabled]="pagesLoading">
                      Load more
                    </button>
                  </div>
                }
              }
            }
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Jobs">
      <div class="tab-section">
        <mat-card class="translations-card">
          <mat-card-header>
              <div class="card-header-row">
                  <div class="card-title-row">
                      <p class="translations-section-title">Translation jobs</p>
                      <span class="translations-muted">Latest 50 jobs for this client.</span>
                  </div>
                  <button (click)="loadJobs()" [disabled]="jobsLoading" color="primary" mat-stroked-button>
                      Refresh jobs
                  </button>
            </div>
          </mat-card-header>
          <mat-card-content>
            @if (jobsLoading) {
              <div class="loading-row">
                <mat-progress-spinner diameter="28" mode="indeterminate"></mat-progress-spinner>
                <span class="translations-muted">Loading jobs...</span>
              </div>
            } @else {
              @if (jobsLoadError) {
                <div class="translations-error" role="alert">
                  <p class="translations-muted">{{ jobsLoadError }}</p>
                  <button type="button" (click)="loadJobs()" [disabled]="jobsLoading" mat-stroked-button>
                    Retry
                  </button>
                </div>
              } @else if (jobs.length === 0) {
                <p class="translations-muted">No translation jobs yet.</p>
              } @else {
                <div class="job-list">
                  @for (job of jobs; track job.id) {
                    <div class="job-card">
                      <div class="job-header">
                        <div>
                          <p class="job-title">{{ job.contentType }} · #{{ job.sourceId }}</p>
                          <p class="translations-muted">
                            {{ job.targetLanguage }} · {{ job.createdAt | date: 'short' }}
                          </p>
                        </div>
                        <span class="status-pill status-{{ job.status.toLowerCase() }}">{{ job.status }}</span>
                      </div>
                      @if (job.targetUrl) {
                        <a class="page-link" [href]="job.targetUrl" target="_blank" rel="noreferrer">
                          Open translated draft
                        </a>
                      }
                      @if (job.logs?.length) {
                        <ul class="warning-list">
                          @for (entry of job.logs; track entry.timestamp + ':' + entry.code + ':' + entry.message) {
                            <li>
                              <strong>{{ entry.level }}</strong>: {{ entry.message }}
                            </li>
                          }
                        </ul>
                      }
                      @if (job.error) {
                        <p class="warn">{{ job.error }}</p>
                      }
                    </div>
                  }
                </div>
              }
            }
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Settings">
      <div class="tab-section">
        <mat-card class="translations-card">
          <mat-card-header>
            <div class="card-title-row">
              <p class="translations-section-title">Client defaults</p>
              <span class="translations-muted">Applied to every translation job for this client.</span>
            </div>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid">
              <mat-form-field appearance="outline">
                <mat-label>Default model</mat-label>
                <input matInput [(ngModel)]="settings.defaultModel" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Form policy</mat-label>
                <mat-select [(ngModel)]="settings.formPolicy">
                  @for (option of policyOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>CTA policy</mat-label>
                <mat-select [(ngModel)]="settings.ctaPolicy">
                  @for (option of policyOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Default instructions</mat-label>
              <textarea matInput rows="6" [(ngModel)]="settings.defaultInstructions"></textarea>
            </mat-form-field>

            <div class="action-row">
              <button mat-flat-button color="primary" (click)="saveSettings()">Save settings</button>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>
  </mat-tab-group>
</section>
